import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Badge } from '../../../components/ui/Badge';

interface ProductCellProps {
  names: string[];
  count: number;
}

/** Horizontal space the "+N" badge needs, including the 4px gap before it. */
const BADGE_SPACE = 34;
const TABLE_FONT = '400 14px Inter, -apple-system, BlinkMacSystemFont, sans-serif';

let measurementContext: CanvasRenderingContext2D | null = null;

function textWidth(text: string): number {
  if (!measurementContext) {
    measurementContext = document.createElement('canvas').getContext('2d');
  }
  if (!measurementContext) {
    // Canvas unavailable (SSR / very old browser): fall back to a rough estimate.
    return text.length * 7.2;
  }
  measurementContext.font = TABLE_FONT;
  return measurementContext.measureText(text).width;
}

/**
 * Decides how many product names fit next to the "+N" badge.
 * The design shows a variable number of names ("Laptop +2" but
 * "Chairs, Tables +7"), so the count is measured rather than hard-coded.
 */
function fitNames(names: string[], available: number): { shown: number; remaining: number } {
  for (let shown = names.length; shown >= 1; shown -= 1) {
    const remaining = names.length - shown;
    const label = names.slice(0, shown).join(', ');
    const needed = textWidth(label) + (remaining > 0 ? BADGE_SPACE : 0);
    if (needed <= available) return { shown, remaining };
  }
  return { shown: 1, remaining: Math.max(names.length - 1, 0) };
}

/**
 * The "Product" column: as many names as fit, then a "+N" pill counting the
 * remainder (matching the design's "Laptop +2" / "Chairs, Tables +7").
 */
export function ProductCell({ names, count }: ProductCellProps) {
  const containerRef = useRef<HTMLSpanElement>(null);
  const [available, setAvailable] = useState(0);

  useLayoutEffect(() => {
    const element = containerRef.current;
    if (!element) return undefined;

    // `available` is the full width of the cell; `fitNames` reserves room for
    // the "+N" pill itself when one is needed.
    const update = () => setAvailable(element.clientWidth);
    update();

    const observer = new ResizeObserver(update);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  // Re-measure once the Inter webfont has loaded so the badge count is stable.
  useEffect(() => {
    let cancelled = false;
    document.fonts?.ready.then(() => {
      if (!cancelled && containerRef.current) {
        setAvailable(containerRef.current.clientWidth);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (names.length === 0) {
    return <span className="text-muted">—</span>;
  }

  const { shown, remaining } = available > 0
    ? fitNames(names, available)
    : { shown: 1, remaining: Math.max(names.length - 1, 0) };

  return (
    <span className="product-cell" ref={containerRef} title={names.join(', ')}>
      <span className="product-cell__names">{names.slice(0, shown).join(', ')}</span>
      {remaining > 0 ? (
        <Badge variant="info" title={`${count} products`}>
          {`+${remaining}`}
        </Badge>
      ) : null}
    </span>
  );
}
